import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Loader2, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const createEmptyFeeItem = () => ({
  program: '',
  annualFee: '',
  totalFee: '',
});

const createEmptyScholarshipItem = () => ({
  name: '',
  coverage: '',
  criteria: '',
});

const FeesScholarshipsForm = ({
  content,
  setContent,
  loading,
  saving,
  onSave,
}) => {
  const [activeFeeItem, setActiveFeeItem] = useState('fee-0');
  const [activeScholarshipItem, setActiveScholarshipItem] = useState('scholarship-0');
  const feeRefs = useRef({});
  const scholarshipRefs = useRef({});

  useEffect(() => {
    setActiveFeeItem((prev) => {
      const idx = Number((prev || '').split('-')[1]);
      const safeIdx = Number.isFinite(idx) && idx < content.fees.length ? idx : 0;
      return content.fees.length ? `fee-${safeIdx}` : '';
    });

    setActiveScholarshipItem((prev) => {
      const idx = Number((prev || '').split('-')[1]);
      const safeIdx = Number.isFinite(idx) && idx < content.scholarships.length ? idx : 0;
      return content.scholarships.length ? `scholarship-${safeIdx}` : '';
    });
  }, [content.fees.length, content.scholarships.length]);

  const handleFeeChange = (index, field, value) => {
    setContent((prev) => ({
      ...prev,
      fees: prev.fees.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleScholarshipChange = (index, field, value) => {
    setContent((prev) => ({
      ...prev,
      scholarships: prev.scholarships.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addFee = () => {
    setContent((prev) => ({
      ...prev,
      fees: [...prev.fees, createEmptyFeeItem()],
    }));

    const newIndex = content.fees.length;
    setActiveFeeItem(`fee-${newIndex}`);

    setTimeout(() => {
      const rowNode = feeRefs.current[newIndex];
      if (rowNode) {
        rowNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const firstInput = rowNode.querySelector('input');
        firstInput?.focus();
      }
    }, 0);
  };

  const removeFee = (index) => {
    setContent((prev) => ({
      ...prev,
      fees: prev.fees.filter((_, i) => i !== index),
    }));
  };

  const addScholarship = () => {
    setContent((prev) => ({
      ...prev,
      scholarships: [...prev.scholarships, createEmptyScholarshipItem()],
    }));

    const newIndex = content.scholarships.length;
    setActiveScholarshipItem(`scholarship-${newIndex}`);

    setTimeout(() => {
      const rowNode = scholarshipRefs.current[newIndex];
      if (rowNode) {
        rowNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const firstInput = rowNode.querySelector('input,textarea');
        firstInput?.focus();
      }
    }, 0);
  };

  const removeScholarship = (index) => {
    setContent((prev) => ({
      ...prev,
      scholarships: prev.scholarships.filter((_, i) => i !== index),
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">International Admissions: Fees & Scholarships</CardTitle>
        <CardDescription>
          Manage fee rows and scholarship rows used on the public page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Tuition Fee Rows</Label>
            <Button type="button" variant="outline" size="sm" onClick={addFee} disabled={loading || saving}>
              <Plus className="h-4 w-4 mr-1" />
              Add Fee
            </Button>
          </div>
          <Accordion type="single" collapsible value={activeFeeItem} onValueChange={setActiveFeeItem} className="border rounded-md px-3">
            {content.fees.map((fee, idx) => {
              const label = fee.program?.trim() || `Fee Row ${idx + 1}`;
              return (
                <AccordionItem key={`fee-${idx}`} value={`fee-${idx}`}>
                  <AccordionTrigger className="py-3">
                    <span className="text-sm font-medium">{label}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div ref={(node) => { feeRefs.current[idx] = node; }} className="space-y-2 pb-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Input
                          value={fee.program}
                          onChange={(e) => handleFeeChange(idx, 'program', e.target.value)}
                          placeholder="Program"
                          disabled={loading || saving}
                        />
                        <Input
                          value={fee.annualFee}
                          onChange={(e) => handleFeeChange(idx, 'annualFee', e.target.value)}
                          placeholder="Annual Fee"
                          disabled={loading || saving}
                        />
                        <Input
                          value={fee.totalFee}
                          onChange={(e) => handleFeeChange(idx, 'totalFee', e.target.value)}
                          placeholder="Total Fee"
                          disabled={loading || saving}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFee(idx)}
                          disabled={loading || saving || content.fees.length <= 1}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Scholarship Rows</Label>
            <Button type="button" variant="outline" size="sm" onClick={addScholarship} disabled={loading || saving}>
              <Plus className="h-4 w-4 mr-1" />
              Add Scholarship
            </Button>
          </div>
          <Accordion type="single" collapsible value={activeScholarshipItem} onValueChange={setActiveScholarshipItem} className="border rounded-md px-3">
            {content.scholarships.map((scholarship, idx) => {
              const label = scholarship.name?.trim() || `Scholarship Row ${idx + 1}`;
              return (
                <AccordionItem key={`scholarship-${idx}`} value={`scholarship-${idx}`}>
                  <AccordionTrigger className="py-3">
                    <span className="text-sm font-medium">{label}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div ref={(node) => { scholarshipRefs.current[idx] = node; }} className="space-y-2 pb-2">
                      <Input
                        value={scholarship.name}
                        onChange={(e) => handleScholarshipChange(idx, 'name', e.target.value)}
                        placeholder="Scholarship Name"
                        disabled={loading || saving}
                      />
                      <Input
                        value={scholarship.coverage}
                        onChange={(e) => handleScholarshipChange(idx, 'coverage', e.target.value)}
                        placeholder="Coverage (e.g. 50%)"
                        disabled={loading || saving}
                      />
                      <Textarea
                        value={scholarship.criteria}
                        onChange={(e) => handleScholarshipChange(idx, 'criteria', e.target.value)}
                        placeholder="Criteria"
                        className="min-h-[80px]"
                        disabled={loading || saving}
                      />
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeScholarship(idx)}
                          disabled={loading || saving || content.scholarships.length <= 1}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={onSave}
            className="bg-medicaps-blue hover:bg-medicaps-blue/90"
            disabled={loading || saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeesScholarshipsForm;
